import { Alert } from '@creditornot/cb-alert';
import { Button } from '@creditornot/cb-button';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { getDac7DownloadLink } from 'apis/merchant-gateway/insights/dac7reports/api';
import { useExternalAccessRights } from 'common/hooks';
import { downloadFromUrl } from 'common/utils/download';
import { usePermissions } from 'common/wolt-permissions';
import { hasPayoutReportsPermission } from 'common/wolt-permissions/permissionUtils';
import {
  ExperienceEntityType,
  ExperienceMerchantEntity,
  ExperienceVenueEntity,
  PageHeader,
  useExperienceModuleContext,
} from 'modules/experience-core';
import { MessagePage } from 'modules/experience-core';
import { useIsDac7ReportEnabled } from 'modules/merchant/hooks/useIsDac7ReportEnabled';

import { IndividualBrandPayouts } from './BrandPayouts';
import { IndividualMerchantPayout } from './MerchantPayouts';
import { VenuePayoutReports } from './PayoutReportPage';
import { VenuePayoutReportActions } from './VenuePayoutReportActions';
import { VenuePayoutReportTypeSwitch } from './VenuePayoutReportTypeSwitch';
import { AnnualReportDownloadStatus, VenuePayoutReportType } from './types';
import { getMerchantPayoutLevelByVenueId } from '../../apis/merchant-payout-service/api';
import { MerchantPayoutLevelInfo, PayoutLevel } from '../../apis/merchant-payout-service/types';

const StyledAlert = styled(Alert)`
  border-radius: 0;
  margin-block-start: 16px;
`;
const AlertContent = styled.div`
  display: flex;
  flex-direction: column;
`;
const ExperienceVenuePayoutReports = ({ experienceVenueEntity }: { experienceVenueEntity: ExperienceVenueEntity }) => {
  const [annualReportDownloadStatus, setAnnualReportDownloadStatus] = useState<AnnualReportDownloadStatus>(
    AnnualReportDownloadStatus.Initial,
  );
  const permissions = usePermissions();
  const externalAccessRights = useExternalAccessRights();
  const [venuePayoutReportType, setVenuePayoutReportType] = useState<VenuePayoutReportType>('reports');
  const [mpsMerchant, setMpsMerchant] = useState<MerchantPayoutLevelInfo | null>(null);

  const venue = experienceVenueEntity.experienceEntityObject;

  const intl = useIntl();

  useEffect(() => {
    if (!venue) {
      return;
    }
    getMerchantPayoutLevelByVenueId(venue.id)
      .then(setMpsMerchant)
      .catch(() => toast('Failed to fetch merchant info'));
  }, [venue]);

  if (venue.type === 'daas') {
    return <MessagePage variant="not_supported" />;
  }

  return hasPayoutReportsPermission(permissions, externalAccessRights, venue) ? (
    <>
      <PageHeader>
        <VenuePayoutReportActions venue={venue} setAnnualReportDownloadStatus={setAnnualReportDownloadStatus} />
      </PageHeader>
      <VenuePayoutReportTypeSwitch
        venuePayoutReportType={venuePayoutReportType}
        setVenuePayoutReportType={setVenuePayoutReportType}
      />

      {mpsMerchant && mpsMerchant.payoutLevel === PayoutLevel.MERCHANT && (
        <StyledAlert variant="info" title={intl.formatMessage({ id: 'tax-reporting.title' })}>
          <AlertContent>
            <p>
              This venue has moved to payouts at the merchant level, please{' '}
              <a target="_blank" href={`/merchant/${mpsMerchant.merchantId}/reports`}>
                click
              </a>{' '}
              here to view the documents
            </p>
          </AlertContent>
        </StyledAlert>
      )}

      <VenuePayoutReports
        venue={venue}
        venuePayoutReportType={venuePayoutReportType}
        annualReportDownloadStatus={annualReportDownloadStatus}
        setAnnualReportDownloadStatus={setAnnualReportDownloadStatus}
      />
    </>
  ) : (
    <MessagePage variant="missing_permissions" />
  );
};

const ExperienceMerchantPayoutReports = ({
  experienceMerchantEntity,
}: {
  experienceMerchantEntity: ExperienceMerchantEntity;
}) => {
  const { formatMessage } = useIntl();
  const {
    country: merchantCountry,
    itemid: { $oid: merchantId },
  } = experienceMerchantEntity.experienceEntityObject;
  const isDac7Enabled = useIsDac7ReportEnabled({ merchantId, merchantCountry });

  const { data: dac7ReportLink } = useQuery(
    ['getDac7DownloadLink', merchantId],
    () => getDac7DownloadLink(merchantId),
    { enabled: isDac7Enabled },
  );

  return (
    <>
      <PageHeader>
        {!!dac7ReportLink && (
          <Button size="small" variant="primary" onClick={() => downloadFromUrl(dac7ReportLink)}>
            {formatMessage({ id: 'payout-reports.download-dac-7-2023-report' })}
          </Button>
        )}
      </PageHeader>
      <IndividualMerchantPayout merchant={experienceMerchantEntity.experienceEntityObject} />
    </>
  );
};
export const ExperiencePayoutReports = () => {
  const { selectedExperienceEntity } = useExperienceModuleContext();
  const { experienceEntityType } = selectedExperienceEntity;
  if (experienceEntityType === ExperienceEntityType.VENUE) {
    return <ExperienceVenuePayoutReports experienceVenueEntity={selectedExperienceEntity} />;
  }

  if (experienceEntityType === ExperienceEntityType.MERCHANT) {
    return <ExperienceMerchantPayoutReports experienceMerchantEntity={selectedExperienceEntity} />;
  }

  if (experienceEntityType === ExperienceEntityType.BRAND) {
    return (
      <>
        <PageHeader />
        <IndividualBrandPayouts brand={selectedExperienceEntity.experienceEntityObject} />
      </>
    );
  }

  if (experienceEntityType === ExperienceEntityType.ENTERPRISE) {
    return <MessagePage variant="not_supported" />;
  }

  return null;
};
